import ProfanityFilter from '../utils/ProfanityFilter.js';

/**
 * ChatBox - DOM-based conversational chat UI component.
 *
 * Reuses the dialog-overlay / dialog-box frame for visual consistency
 * with the static dialog system. Back arrow returns to dialog menu.
 *
 * Structure:
 *   .dialog-overlay               (shared full-screen backdrop)
 *     .dialog-box.dialog-box--chat (same frame, chat modifier)
 *       .dialog-chat-header       (back arrow + speaker name)
 *       .chat-messages            (scrollable message area)
 *         .chat-message--npc      (NPC messages, left-aligned)
 *         .chat-message--user     (User messages, right-aligned)
 *         .chat-loading           (typing indicator dots)
 *       .chat-input-row           (text input + send button)
 */
export default class ChatBox {
  constructor() {
    this.element = null;
    this._messagesEl = null;
    this._inputEl = null;
    this._sendBtn = null;
    this._loadingEl = null;
    this._speakerEl = null;
    this._backBtn = null;

    this._visible = false;
    this._isLoading = false;

    this._onSend = null;
    this._onClose = null;

    // Typewriter state for NPC messages
    this._typewriterTimer = null;
    this._typewriterEl = null;
    this._typewriterText = '';
    this._typewriterIndex = 0;

    this._onKeyDown = this._handleKeyDown.bind(this);

    this._createElement();
  }

  // ====== Public API ======

  /**
   * Show the chat box for an NPC.
   * @param {string} speakerName
   * @param {Array<{role: string, content: string}>} history - Past messages (excluding system)
   * @param {string|null} greeting - Greeting message to show on first open
   * @param {Function} onSend - Called with user message text
   * @param {Function} onClose - Called when user closes chat
   */
  show(speakerName, history, greeting, onSend, onClose) {
    this._onSend = onSend;
    this._onClose = onClose;
    this._speakerEl.textContent = speakerName;

    // Clear and re-render messages
    this._clearMessages();

    // Show greeting on first open (with typewriter)
    if (greeting) {
      this._appendMessageTypewriter(ProfanityFilter.filterAlways(greeting));
    }

    for (const msg of history) {
      if (msg.role === 'system') continue;
      this._appendMessage(msg.role === 'user' ? 'user' : 'npc', ProfanityFilter.filterAlways(msg.content));
    }

    this._visible = true;
    this.element.style.display = 'flex';
    this._inputEl.value = '';
    this._sendBtn.disabled = false;
    this._setLoading(false);

    window.addEventListener('keydown', this._onKeyDown, true);

    // Auto-focus input after a short delay (helps mobile keyboards)
    setTimeout(() => this._inputEl.focus(), 100);
    this._scrollToBottom();
  }

  hide() {
    this._visible = false;
    this._finishTypewriter();
    this.element.style.display = 'none';
    this._inputEl.blur();
    this._onSend = null;
    this._onClose = null;
    window.removeEventListener('keydown', this._onKeyDown, true);
  }

  /**
   * Add a single message to the display.
   * NPC messages use typewriter effect with dialog blip sounds.
   * @param {'user'|'assistant'} role
   * @param {string} text
   */
  addMessage(role, text) {
    const filtered = ProfanityFilter.filterAlways(text);
    const type = role === 'user' ? 'user' : 'npc';
    if (type === 'npc') {
      this._finishTypewriter(); // finish any in-progress typewriter
      this._appendMessageTypewriter(filtered);
    } else {
      this._appendMessage(type, filtered);
    }
    this._scrollToBottom();
  }

  /**
   * Show/hide loading indicator.
   * @param {boolean} loading
   */
  setLoading(loading) {
    this._setLoading(loading);
  }

  /**
   * Show an error message in the chat.
   * @param {string} errorText
   */
  showError(errorText) {
    const el = document.createElement('div');
    el.className = 'chat-message chat-message--error';
    el.textContent = errorText;
    this._messagesEl.insertBefore(el, this._loadingEl);
    this._scrollToBottom();
  }

  isVisible() {
    return this._visible;
  }

  destroy() {
    window.removeEventListener('keydown', this._onKeyDown, true);
    if (this.element.parentElement) {
      this.element.parentElement.removeChild(this.element);
    }
  }

  // ====== Private ======

  _createElement() {
    // Reuse dialog-overlay for visual consistency
    this.element = document.createElement('div');
    this.element.className = 'dialog-overlay';
    this.element.style.display = 'none';

    // Click outside the box (on overlay) to go back
    this.element.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (this._onClose) this._onClose();
    });

    // Reuse dialog-box frame with chat modifier
    const box = document.createElement('div');
    box.className = 'dialog-box dialog-box--chat';
    box.addEventListener('click', (e) => e.stopPropagation());

    // Header — back arrow + speaker name (matches dialog speaker style)
    const header = document.createElement('div');
    header.className = 'dialog-chat-header';

    this._backBtn = document.createElement('button');
    this._backBtn.className = 'dialog-chat-back';
    this._backBtn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    this._backBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this._onClose) this._onClose();
    });

    this._speakerEl = document.createElement('div');
    this._speakerEl.className = 'dialog-speaker';

    header.appendChild(this._speakerEl);
    header.appendChild(this._backBtn);

    // Messages area
    this._messagesEl = document.createElement('div');
    this._messagesEl.className = 'chat-messages';

    // Loading indicator (always in DOM, toggled via class)
    this._loadingEl = document.createElement('div');
    this._loadingEl.className = 'chat-loading';
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement('div');
      dot.className = 'chat-loading-dot';
      this._loadingEl.appendChild(dot);
    }
    this._messagesEl.appendChild(this._loadingEl);

    // Input row
    const inputRow = document.createElement('div');
    inputRow.className = 'chat-input-row';

    this._inputEl = document.createElement('input');
    this._inputEl.className = 'chat-input';
    this._inputEl.type = 'text';
    this._inputEl.placeholder = 'Say something...';
    this._inputEl.autocomplete = 'off';
    this._inputEl.spellcheck = false;

    this._sendBtn = document.createElement('button');
    this._sendBtn.className = 'chat-send-btn';
    this._sendBtn.textContent = 'Send';
    this._sendBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._handleSend();
    });

    inputRow.appendChild(this._inputEl);
    inputRow.appendChild(this._sendBtn);

    // Assemble
    box.appendChild(header);
    box.appendChild(this._messagesEl);
    box.appendChild(inputRow);
    this.element.appendChild(box);

    document.body.appendChild(this.element);
  }

  _handleSend() {
    if (this._isLoading) return;

    const text = this._inputEl.value.trim();
    if (!text) return;

    this._inputEl.value = '';
    this._appendMessage('user', ProfanityFilter.filterAlways(text));
    this._scrollToBottom();

    if (this._onSend) {
      this._onSend(text);
    }

    // Re-focus input for next message
    this._inputEl.focus();
  }

  _handleKeyDown(e) {
    // Always stop propagation to prevent game input
    e.stopPropagation();

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this._handleSend();
    }
    // Escape is handled by DialogManager
  }

  _appendMessage(type, text) {
    const el = document.createElement('div');
    el.className = `chat-message chat-message--${type}`;
    el.textContent = text;
    // Insert before loading indicator
    this._messagesEl.insertBefore(el, this._loadingEl);
  }

  _appendMessageTypewriter(text) {
    const el = document.createElement('div');
    el.className = 'chat-message chat-message--npc';
    el.textContent = '';
    this._messagesEl.insertBefore(el, this._loadingEl);

    this._typewriterEl = el;
    this._typewriterText = text;
    this._typewriterIndex = 0;
    this._typewriterNext();

    // Click on the message to skip
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      this._finishTypewriter();
    });
  }

  _typewriterNext() {
    if (this._typewriterIndex >= this._typewriterText.length) {
      this._typewriterEl = null;
      return;
    }

    this._typewriterIndex++;
    this._typewriterEl.textContent = this._typewriterText.substring(0, this._typewriterIndex);

    // Play dialog blip every 3rd character (matches DialogBox)
    if (this._typewriterIndex % 3 === 1) {
      window.game?.audioSystem?.audio?.playUiDialog();
    }

    this._scrollToBottom();
    this._typewriterTimer = setTimeout(() => this._typewriterNext(), 30);
  }

  _finishTypewriter() {
    if (this._typewriterTimer !== null) {
      clearTimeout(this._typewriterTimer);
      this._typewriterTimer = null;
    }
    if (this._typewriterEl) {
      this._typewriterEl.textContent = this._typewriterText;
      this._typewriterEl = null;
    }
  }

  _clearMessages() {
    // Remove all messages but keep the loading indicator
    const messages = this._messagesEl.querySelectorAll('.chat-message');
    messages.forEach(m => m.remove());
  }

  _setLoading(loading) {
    this._isLoading = loading;
    this._loadingEl.classList.toggle('visible', loading);
    this._sendBtn.disabled = loading;
    if (loading) this._scrollToBottom();
  }

  _scrollToBottom() {
    requestAnimationFrame(() => {
      this._messagesEl.scrollTop = this._messagesEl.scrollHeight;
    });
  }
}
